import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import AgentArtistInviteBand from '@/components/landing/AgentArtistInviteBand';
import { ArrowLeft, Music, Sparkles } from 'lucide-react';

const MobileLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const { signIn } = useAuth();
  const timeoutRef = useRef(null);
  const [searchParams] = useSearchParams();
  const signupWithAgentHref = useMemo(() => {
    const a = searchParams.get('agent')?.trim();
    return a ? `/signup?agent=${encodeURIComponent(a.toUpperCase())}` : '/signup';
  }, [searchParams]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    try {
      // Set a maximum timeout for the entire sign-in process
      const signInPromise = signIn(email, password);
      const maxTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Sign-in timeout')), 10000)
      );
      
      const { error } = await Promise.race([signInPromise, maxTimeout]);
      
      // If there's an error, reset loading state
      if (error) {
        setLoading(false);
        return;
      }
      
      // If successful, navigation will happen in signIn
      // Add a shorter timeout to reset loading state if navigation takes too long
      timeoutRef.current = setTimeout(() => {
        setLoading(false);
        // If still on login page after timeout, force navigation
        if (window.location.pathname.includes('/login')) {
          console.warn('⚠️ Navigation seems to have failed, redirecting manually');
          window.location.href = '/admin';
        }
      }, 3000); // Reset after 3 seconds max to prevent infinite loading
    } catch (err) {
      console.error('Login error:', err);
      setLoading(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const handleForgotPassword = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      alert('Please enter your email address first.');
      return;
    }

    setResetSending(true);
    try {
      const { auth } = await import('@/lib/firebase');
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(auth, trimmedEmail);
      alert('If an account exists for this email, a password reset link has been sent.');
    } catch (err) {
      console.error('Password reset error (mobile):', err);
      alert(err.message || 'Unable to send reset email. Please try again.');
    } finally {
      setResetSending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Artist Login - Your Song Request</title>
        <meta name="description" content="Login to your artist account and manage your song requests, setlist, and performances." />
        <meta property="og:title" content="Artist Login - Your Song Request" />
        <meta property="og:description" content="Login to your artist account and manage your song requests, setlist, and performances." />
      </Helmet>

      <div className="min-h-screen bg-black flex flex-col">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-cyan-900/20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(168,85,247,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(6,182,212,0.1),transparent_50%)]"></div>
        </div>

        <div className="relative z-10 w-full shrink-0 px-4 pt-6">
          <AgentArtistInviteBand mode="login" />
        </div>

        {/* Header */}
        <div className="relative z-10 flex-shrink-0 pt-4 pb-4 px-4">
          <Link to="/" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="text-sm">Back to Home</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.5)]">
                  <Music className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-1 -right-1">
                  <Sparkles className="w-4 h-4 text-yellow-400 animate-pulse" />
                </div>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Artist Login</h1>
            <p className="text-cyan-200 text-sm">
              Access your artist dashboard
            </p>
          </motion.div>
        </div>

        {/* Form */}
        <div className="relative z-10 flex-1 px-4 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-sm mx-auto"
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-cyan-100 text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-gray-900/50 border border-cyan-500/30 text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-cyan-400/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                  placeholder="your@email.com"
                />
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-cyan-100 text-sm font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-gray-900/50 border border-cyan-500/30 text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-cyan-400/20 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                  placeholder="••••••••"
                />
              </div>

            {/* Forgot password */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={resetSending || loading}
                className="text-xs text-cyan-300 hover:text-cyan-200 disabled:opacity-50"
              >
                {resetSending ? 'Sending reset link…' : 'Forgot your password?'}
              </button>
            </div>

              {/* Submit Button */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="pt-2"
              >
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold py-3 rounded-xl shadow-[0_0_20px_rgba(6,182,212,0.3)] border border-cyan-400/30"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </motion.div>
            </form>

            {/* Signup Link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-6 text-center"
            >
              <p className="text-gray-400 text-sm">
                Don't have an account?{' '}
                <Link to={signupWithAgentHref} className="text-cyan-400 hover:text-cyan-300 font-semibold">
                  Sign up here
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default MobileLoginPage;

